export interface FlightSearchFormData {
  flightFrom: string;
  flightTo: string;
  departDate: string;
  returnDate: string;
  travelersCount: number;
  travelClass: TravelClass;
  flightFrom_details?: AirportDetails;
  flightTo_details?: AirportDetails;
}

export interface AirportDetails {
  name: string;
  iata_code: string;
}

export type TravelClass = 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST';

export interface FlightSearchPayload {
  flight_from: string;
  flight_to: string;
  depart_date: string;
  return_date?: string;
  travelers_count: number;
  travel_class: TravelClass;
}

export type FlightSearchRequest = {
  payload: FlightSearchPayload;
  page?: number;
  searchFilters?: FlightFilters;
};

export interface FlightFilterOptionsResponse {
  airlines: AirlineOption[];
  airports: AirportOption[];
}

export interface FlightSearchApiError {
  code?: string;
  detail?: string;
  attr?: string | null;
}

export interface FlightSearchResult {
  count?: number;
  next?: string | null;
  previous?: string | null;
  flight_items: FlightItem[];
  currency?: string;
  airline_options?: AirlineOption[];
  airport_options?: AirportOption[];
  errors?: FlightSearchApiError[];
}

export interface FlightItem {
  depart_departure_date_time: string;
  depart_arrival_date_time: string;
  return_departure_date_time?: string;
  return_arrival_date_time?: string;
  depart_departure_airport: string;
  depart_departure_airport_name: string;
  depart_arrival_airport: string;
  depart_arrival_airport_name: string;
  return_departure_airport?: string;
  return_departure_airport_name?: string;
  return_arrival_airport?: string;
  return_arrival_airport_name?: string;
  depart_flight_number: string;
  return_flight_number?: string;
  depart_stop_number: number;
  return_stop_number?: number;
  depart_airline: string;
  return_airline?: string;
  depart_airline_name: string;
  return_airline_name?: string;
  depart_duration: string;
  return_duration?: string;
  depart_stops_airports?: string;
  return_stops_airports?: string;
  price: number;
  currency?: string;
  link?: string;
}

export interface AirlineOption {
  name: string;
  iata_code: string;
}

export interface AirportOption {
  name: string;
  iata_code: string;
}

export interface FlightToIdeaPayload {
  depart_departure_date_time: string;
  depart_arrival_date_time: string;
  return_departure_date_time: string;
  return_arrival_date_time: string;
  depart_departure_airport: string;
  depart_arrival_airport: string;
  return_departure_airport: string;
  return_arrival_airport: string;
  depart_flight_number: string;
  return_flight_number: string;
  depart_airline: string;
  return_airline: string;
  depart_airline_name: string;
  return_airline_name: string;
  price: number;
  travelers_count: number;
  travel_class: string;
  external_id: string;
  discover_payload: string;
}

export interface FlightToIdeaResponse {
  code: string;
  message: string;
  idea_list_name: string;
}

export interface FlightToTripResponse {
  code: string;
  message: string;
  trip_name?: string;
}

export type SortOption = 'price' | 'duration' | 'departure_time';
export type SortOrder = 'asc' | 'desc';
export type StopOption = 'nonstop' | 'one_stop' | 'two_plus';

export interface FlightFilters {
  sortBy: SortOption;
  sortOrder: SortOrder;
  stops: StopOption[];
  airlines: string[];
  airports: string[];
  timeRange: {
    startFrom: string;
    upTo: string;
  };
}

export const FLIGHT_SEARCH_INITIAL_VALUES: FlightSearchFormData = {
  flightFrom: '',
  flightTo: '',
  departDate: '',
  returnDate: '',
  travelersCount: 1,
  travelClass: 'ECONOMY',
  flightFrom_details: undefined,
  flightTo_details: undefined,
};

export const FLIGHT_FILTERS_INITIAL_VALUES: FlightFilters = {
  sortBy: 'departure_time',
  sortOrder: 'asc',
  stops: [],
  airlines: [],
  airports: [],
  timeRange: {
    startFrom: '',
    upTo: '',
  },
};

export const TRAVEL_CLASS_OPTIONS: { value: TravelClass; label: string }[] = [
  { value: 'ECONOMY', label: 'Economy' },
  { value: 'PREMIUM_ECONOMY', label: 'Premium Economy' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'FIRST', label: 'First' },
];

export type FlightSortSelectValue = `${SortOption}:${SortOrder}`;

export const FLIGHT_SORT_SELECT_OPTIONS: { value: FlightSortSelectValue; label: string }[] = [
  { value: 'price:asc', label: 'Price (asc)' },
  { value: 'price:desc', label: 'Price (desc)' },
  { value: 'duration:asc', label: 'Duration (asc)' },
  { value: 'duration:desc', label: 'Duration (desc)' },
  { value: 'departure_time:asc', label: 'Departure (asc)' },
  { value: 'departure_time:desc', label: 'Departure (desc)' },
];

export function toFlightSortSelectValue(sortBy: SortOption, sortOrder: SortOrder = 'asc'): FlightSortSelectValue {
  return `${sortBy}:${sortOrder}`;
}

export function parseFlightSortSelectValue(value: string): { sortBy: SortOption; sortOrder: SortOrder } {
  const colonIndex = value.lastIndexOf(':');
  const sortByRaw = colonIndex >= 0 ? value.slice(0, colonIndex) : value;
  const sortOrderRaw = colonIndex >= 0 ? value.slice(colonIndex + 1) : 'asc';
  const validSortBy: SortOption[] = ['price', 'duration', 'departure_time'];
  const validSortOrder: SortOrder[] = ['asc', 'desc'];

  return {
    sortBy: validSortBy.includes(sortByRaw as SortOption) ? (sortByRaw as SortOption) : 'departure_time',
    sortOrder: validSortOrder.includes(sortOrderRaw as SortOrder) ? (sortOrderRaw as SortOrder) : 'asc',
  };
}

export function flightFiltersToOrderingParam(filters: Pick<FlightFilters, 'sortBy' | 'sortOrder'>): string {
  const { sortBy, sortOrder } = normalizeFlightFilters({
    ...FLIGHT_FILTERS_INITIAL_VALUES,
    ...filters,
  });
  return sortOrder === 'desc' ? `-${sortBy}` : sortBy;
}

export function normalizeFlightFilters(filters: FlightFilters): FlightFilters {
  const validSortBy: SortOption[] = ['price', 'duration', 'departure_time'];
  return {
    ...filters,
    sortBy: validSortBy.includes(filters.sortBy) ? filters.sortBy : 'departure_time',
    sortOrder: filters.sortOrder === 'desc' ? 'desc' : 'asc',
  };
}

export const STOP_OPTIONS: { value: StopOption; label: string }[] = [
  { value: 'nonstop', label: 'Nonstop' },
  { value: 'one_stop', label: '1 stop' },
  { value: 'two_plus', label: '2+ stop' },
];
